import { ElementController, selectFrom } from 'js-common';
import Flickity from 'flickity';

/**
 * Controller for news and event cards slider.
 *
 * @extends {ElementController}
 */
export default class ImageSlider extends ElementController {
  static id = 'ImageSlider';

  /**
   * DOM and images loaded, instantiate slider.
   */
  load() {
    // Init slider
    this.slider = new Flickity(this.getRoot(), this.getSliderOptions());

    // Get focusable elements for each cell for toggling in checkFocusable.
    // Exclude elements that are already disabled and should stay that way.
    this.cellFocusables = this.slider.cells.map((cell) =>
      selectFrom(cell.element, this.getFocusableSelector()).filter(
        (control) => control.tabIndex !== -1,
      ),
    );

    // Run initial pass on page load
    this.checkFocusable();

    // Bind events
    this.slider.on('select', this.handleCellChange);
  }

  /**
   * Get selector for focusable elements.
   *
   * @returns {string}
   */
  getFocusableSelector() {
    return 'a, button';
  }

  /**
   * Get Flickity slider options.
   *
   * @returns {object}
   */
  getSliderOptions() {
    return {
      cellAlign: 'center',
      contain: true,
      groupCells: false,
      pageDots: false,
      prevNextButtons: false,
      draggable: false,
      wrapAround: true,
      pauseAutoPlayOnHover: false,
      autoPlay: 8000,
    };
  }

  /**
   * Enable and disable tabbing on cell elements depending on aria-hidden
   * (which is handled by Flickity).
   */
  checkFocusable = () => {
    this.slider.cells.forEach((cell, i) => {
      const focusables = this.cellFocusables[i];
      const isDisabled = cell.element.getAttribute('aria-hidden') === 'true';
      focusables.forEach((el) => {
        /* eslint-disable no-param-reassign */
        el.tabIndex = isDisabled ? -1 : 0;
      });
    });
  };

  /**
   * Check focusables when the current active cell is changed.
   */
  handleCellChange = () => {
    this.checkFocusable();
  };
}
